import React, { useEffect } from 'react'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { Typography } from '~/components/typography'
import { Box, CircularProgress, Dialog, Grid, Slide } from '@material-ui/core'
import { appStore, authStore } from '~/stores'
import { quickApplyModalStyled } from '~/features/quick-apply/quick-apply-component.style'
import { ChecklistsTab, ReferenceTab, WorkTab } from '~/components/additional-info-apply-dialog/tabs'
import { additionalInfoApplyDialogStore } from '~/components/additional-info-apply-dialog/additional-info-apply-dialog.store'
import { loadingState, observer } from '~/common/mobx.decorator'
import { quickApplyStore } from '~/features/quick-apply/quick-apply.store'
import { isEmpty, isNil } from 'lodash'
import { Step, StepLabel, Stepper } from '~/components/stepper'
import { SvgDoneV2Svg } from '~/components/icons'
import styled, { css } from 'styled-components'
import { NotifyDialog } from '~/components/notify'
import { careJobDetailStore } from '~/features/care-jobs/detail/care-job-detail.store'
import { useEffectOnce } from 'react-use'
import { logClickEvent, logTrackingScreenEvent } from '~/common/tracking/event-client.tracking'
import { COMMON_EVENT_CLICK_TRACKING } from '~/common/constants/tracking.constant'

const WrapperBox = styled(Box)`
	.float-stepper.floating {
		position: fixed;
		z-index: 999;
		background: #ffffff;
		right: 0px;
		top: 0px;
		width: calc(100% - 280px);
		padding: 15px;
		min-height: 73px;
		box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
		@media (max-width: 960px) {
			width: 100%;
		}
	}

	.MuiStepConnector-horizontal {
		visibility: hidden;
	}

	.MuiStepLabel-horizontal {
		position: relative;
	}
`

export const activeStepStyled = css`
	.MuiCircularProgress-root {
		border: 2px solid #c2daf0;
	}

	.MuiStepIcon-text {
		fill: ${({ theme }) => theme.palette.primary.main};
	}

	.MuiStepIcon-root {
		font-size: 24px !important;
	}

	.MuiStepIcon-root.MuiStepIcon-active {
		color: #ddefff !important;
		font-size: 24px !important;

		.MuiStepIcon-text {
			fill: ${({ theme }) => theme.palette.primary.main} !important;
		}
	}

	.MuiStepLabel-labelContainer {
		position: absolute;
		background: ${({ theme }) => theme.palette.primary.main};
		width: 100%;
		height: 2px;
		bottom: -16px;
	}
`

export const activeStepNumberStyled = css``

export const AdditionalInfoApplyDialogComponent = observer(() => {
	const { t } = useTranslation()
	const {
		lookupAccountScl,
		lookupSkillChecklistsByJob,
		slideDirection,
		fetchLookupSkillChecklistsByJob,
		fetchLookupAccountScl,
		fetchWorkerSkillChecklists,
		changeActiveStepAndSubmit,
		stepIndex,
		showSuccessSkillCheckListDialog,
		setShowSuccessSkillCheckListDialog,
		changeActiveStep,
		percentPerStep,
		fetchWorkerSclProvider,
		steps,
		fetchWorkerReferences,
		fetchWorkerResumes,
		resetInitData,
	} = additionalInfoApplyDialogStore
	const { showAdditionInfo, setShowAdditionInfo, currentJobID, applyJobData, currentJob } = quickApplyStore

	const { providerId } = lookupAccountScl || {}
	const { email } = applyJobData || {}
	const { id } = authStore
	const loadingLookupSkillChecklistsByJob = loadingState('additionalInfoApplyDialogStore/fetchLookupSkillChecklistsByJob')

	const STEPS = lookupSkillChecklistsByJob?.length === 0 ? [WorkTab, ReferenceTab] : [WorkTab, ReferenceTab, ChecklistsTab]

	useEffectOnce(() => {
		showAdditionInfo && logTrackingScreenEvent(COMMON_EVENT_CLICK_TRACKING.additionalInfoForApplicationScreen, { is_start_screen_session: true })
	})

	useEffect(() => {
		if (showAdditionInfo && !!currentJobID) {
			fetchLookupSkillChecklistsByJob(currentJobID)
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showAdditionInfo, currentJobID])

	useEffect(() => {
		if (!isEmpty(lookupSkillChecklistsByJob) && isEmpty(id) && !isEmpty(email)) {
			fetchLookupAccountScl({ email })
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lookupSkillChecklistsByJob, email, id])
	useEffect(() => {
		if (!isEmpty(providerId) && isEmpty(id)) {
			fetchWorkerSkillChecklists({ providerAccount: providerId })
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, providerId])

	useEffectOnce(() => {
		if (!isEmpty(stepIndex)) {
			changeActiveStep(0)
		}
	})
	useEffect(() => {
		if (authStore.id) {
			fetchWorkerReferences()
			fetchWorkerResumes()
		}
		resetInitData()
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showAdditionInfo])

	return (
		<>
			<Dialog
				open={showAdditionInfo}
				onClose={async () => {
					await setShowAdditionInfo(false)
					await changeActiveStep(0)
				}}
				css={quickApplyModalStyled}
			>
				<Box m={2}>
					<Grid container className="wrap-title">
						<Grid item xs={12} sm={12} className="title">
							<Typography>{currentJob?.atsSource === 'symplr' ? t('ADDITIONAL_INFO_APPLICANT_SL_TITLE') : t('ADDITIONAL_INFO_APPLICANT_TITLE')}</Typography>
						</Grid>
						<Grid
							style={{
								margin: '24px 0',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
								background: appStore.getTheme.colors.background.element.veryLight,
								borderRadius: 8,
								padding: 16,
								alignItems: 'start',
							}}
							item
							xs={12}
						>
							<Typography
								css={[
									appStore.getTheme.typography.p1,
									{
										color: appStore.getTheme.colors.content.dark,
										margin: '10px 0',
										fontWeight: 500,
									},
								]}
							>
								{t('ADDITIONAL_SKIP')}
							</Typography>
							{(!currentJob?.isRequiredResume || stepIndex > 0) && (
								<Typography
									onClick={async () => {
										if (!authStore.id) {
											await quickApplyStore.quickApplyJobs(applyJobData, {}, () => setShowAdditionInfo(false))
										} else {
											await careJobDetailStore.actionApplyJob().then((res) => {
												careJobDetailStore.setJobItem(res)
											})
											await setShowAdditionInfo(false)
											await changeActiveStep(0)
										}
										logClickEvent(COMMON_EVENT_CLICK_TRACKING.skipNApplyLink, {
											job_id: currentJobID,
											is_account_created: false, // false HERE
											step_id: stepIndex, // 1 HERE. Step 1, 2, OR 3 according to Figma
											has_una: !isEmpty(fetchWorkerSclProvider), // null HERE. whether user's email has associated UNA account or not
											has_skills_assigned: !isEmpty(lookupSkillChecklistsByJob), // null HERE
										})
									}}
									css={[
										appStore.getTheme.typography.p1,
										{
											color: appStore.getTheme.colors.secondary.main,
											cursor: 'pointer',
										},
									]}
								>
									{currentJob?.atsSource === 'symplr' ? t('SKILL_AND_REQUEST') : t('SKIP_AND_APPLY')}
								</Typography>
							)}
						</Grid>
					</Grid>
					{loadingLookupSkillChecklistsByJob ? (
						<Box mt={2} display="flex" justifyContent="center">
							<CircularProgress />
						</Box>
					) : (
						<div>
							<WrapperBox>
								<div className={`float-stepper`}>
									<Box p={2} borderBottom={1}>
										<Stepper activeStep={stepIndex} nonLinear>
											{steps.map(({ id, hideStep, eventIdHorizon }, index) => {
												const percent = percentPerStep?.[id]
												return (
													!hideStep && (
														<Step
															totalStep={steps?.length}
															key={id}
															completed={percent === 100}
															disabled={stepIndex === 3}
															onMouseDown={() => {
																const hasResumes = additionalInfoApplyDialogStore.resumes?.filter((r) => !r._destroy).length > 0
																const isMovingForward = index > stepIndex

																if (stepIndex === 0 && isMovingForward && quickApplyStore.currentJob?.isRequiredResume && !hasResumes) {
																	return
																}
																changeActiveStepAndSubmit(index)
															}}
															css={stepIndex === index && activeStepStyled}
														>
															{percent === 100 && (
																<StepLabel StepIconComponent={SvgDoneV2Svg} variant={percent === 0 && index !== stepIndex && 'secondary'} alternativeLabel>
																	{stepIndex === index && <div className="line"></div>}
																</StepLabel>
															)}
															{percent !== 100 && (
																<StepLabel
																	css={stepIndex === index && activeStepNumberStyled}
																	variant={percent === 0 && index !== stepIndex && 'secondary'}
																	alternativeLabel
																>
																	{stepIndex === index && <div className="line"></div>}
																</StepLabel>
															)}
														</Step>
													)
												)
											})}
										</Stepper>
									</Box>
								</div>
								{STEPS.map((StepDetail, index) => (
									<Slide key={index} direction={slideDirection} in={stepIndex === index}>
										<Box>{index === stepIndex && <StepDetail onNextTab={() => changeActiveStepAndSubmit(index + 1)} />}</Box>
									</Slide>
								))}
							</WrapperBox>
						</div>
					)}
				</Box>
			</Dialog>

			<NotifyDialog
				open={showSuccessSkillCheckListDialog}
				onClose={() => {
					setShowSuccessSkillCheckListDialog(false)
				}}
				onOk={async () => {
					await setShowSuccessSkillCheckListDialog(false)
					await quickApplyStore.setAutoAssignScl(true)
					await quickApplyStore.quickApplyJobs(
						{
							...applyJobData,
							resumes:
								additionalInfoApplyDialogStore.resumes
									.filter((item) => item.signedBlobId && !item._destroy && !item.parentId)
									?.map((item) => ({
										fileSignedBlobId: item.signedBlobId,
									})) || [],
							references: additionalInfoApplyDialogStore.references?.map((item) => {
								return {
									id: item.id,
									email: item.email,
									firstName: item.firstName,
									lastName: item.lastName,
									phone: item.phone,
									relationship: item.relationship,
									jobTitle: item.jobTitle,
									state: item.location?.stateCode ?? null,
									city: item.location?.name ?? null,
								}
							}),
						},
						{},
						() => setShowAdditionInfo(false)
					)
					await logClickEvent(COMMON_EVENT_CLICK_TRACKING.confirmToApplyBtnApplyForThisJob, {
						job_id: currentJobID,
						is_account_created: false, // false HERE
						step_id: stepIndex, // 1 HERE. Step 1, 2, OR 3 according to Figma
						has_una: !isEmpty(fetchWorkerSclProvider), // null HERE. whether user's email has associated UNA account or not
						has_skills_assigned: !isEmpty(lookupSkillChecklistsByJob), // null HERE
					})
				}}
				onCancel={() => {
					setShowSuccessSkillCheckListDialog(false)
					logClickEvent(COMMON_EVENT_CLICK_TRACKING.cancelBtn, {
						job_id: currentJobID,
						is_account_created: false, // false HERE
						step_id: stepIndex, // 1 HERE. Step 1, 2, OR 3 according to Figma
						has_una: !isEmpty(fetchWorkerSclProvider), // null HERE. whether user's email has associated UNA account or not
						has_skills_assigned: !isEmpty(lookupSkillChecklistsByJob), // null HERE
					})
				}}
				okText={'Confirm and apply'}
				cancelText={'Cancel'}
				title={!isNil(lookupAccountScl) ? t('SUCCESS_CHECKLIST_UNA_TITLE') : t('SUCCESS_CHECKLIST_WITHOUT_UNA_TITLE')}
				children={!isNil(lookupAccountScl) ? t('SUCCESS_CHECKLIST_UNA_DES') : t('SUCCESS_CHECKLIST_WITHOUT_UNA_DES')}
			/>
		</>
	)
})
